// src/app/analytics/analytics.service.ts
import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { debounceTime, filter, throttleTime } from 'rxjs/operators';
import { fromEvent } from 'rxjs';
import * as amplitude from '@amplitude/analytics-browser';
import { environment } from 'src/environments/environment';
import { AppConfigService } from '../_services/app-config.service';

import { EVENTS } from './events';
import { PROPERTIES } from './properties';
import { isExternalUser } from '../utilities/utils';


export const MAPTILER_CACHE_STATUS = 'MAPTILER_CACHE_STATUS';

@Injectable({
  providedIn: 'root',
})
export class EventTrackingServcie {
  public userId: string | null = null;
  // public isExternalUser: boolean = false;
  private sessionId: string | undefined;
  private previousUrl: string | null = null;
  private amplitudeInitialized = false;

  constructor(private router: Router, private appConfigService: AppConfigService) {
    // Initialize session
    this.initSession();
    this.initUserActivityTracking();
    // Track page views
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navEnd = event as NavigationEnd;
        this.track(EVENTS.PAGE_VIEWED, {
          [PROPERTIES.USER_ID]: this.userId,
          [PROPERTIES.PAGE_NAME]: navEnd.urlAfterRedirects,
          [PROPERTIES.REFERRER]: this.previousUrl,
          [PROPERTIES.TIMESTAMP]: new Date().toISOString(),
        });
        this.previousUrl = navEnd.urlAfterRedirects;
      });

    // Detect hard refresh
    this.detectHardRefresh();

    // Track session end on unload
    window.addEventListener('beforeunload', () => this.endSession());
  }

  /**
   * Amplitude needs a key from AppConfigService, which only loads once a user
   * logs in (this service itself is constructed at app bootstrap, before that).
   * Cheap no-op once initialized; called from setUserId/track so it picks up
   * the key the first time either fires after login.
   */
  private tryInitAmplitude() {
    // Gated on enableAmplitude, not `production`. Angular's prod-mode flag is
    // now enabled in UAT too (so production-only defects surface before
    // release); reusing it here would have started reporting UAT traffic into
    // the production Amplitude account. See src/environments/environment.prod.ts.
    if (this.amplitudeInitialized || !environment.enableAmplitude) {
      return;
    }
    const amplitudeApiKey = this.appConfigService.config?.amplitudeApiKey;
    if (amplitudeApiKey) {
      amplitude.init(amplitudeApiKey, { defaultTracking: { sessions: false }, serverZone: 'EU' });
      this.amplitudeInitialized = true;
    }
  }

  /** Set the current user ID */
  public setUserId(id: string) {
    this.userId = id;
    this.tryInitAmplitude();
    if (this.amplitudeInitialized) {
      amplitude.setUserId(id);
    }
  }

  /** Track any custom event */
  public track(eventName: string, properties: Record<string, any>) {
    //This are common properties for all events
    properties[PROPERTIES.IS_EXTERNAL] = isExternalUser(this.userId || '');
    properties[PROPERTIES.USER_ID] = this.userId;
    // properties[PROPERTIES.TIMESTAMP] = new Date().toISOString();
    this.tryInitAmplitude();
    //fire event only where analytics are enabled, and only once Amplitude has a key to init with
    if(environment.enableAmplitude && this.amplitudeInitialized){
      amplitude.track(eventName, properties);
    }
  }

  /** Initialize session */
  private initSession() {
    this.sessionId = localStorage.getItem(PROPERTIES.SESSION_ID) || this.generateSessionId();
    localStorage.setItem(PROPERTIES.SESSION_ID, this.sessionId);
    this.captureDeviceInfo();
    this.track(EVENTS.SESSION_STARTED, {
      [PROPERTIES.USER_ID]: this.userId,
      [PROPERTIES.SESSION_ID]: this.sessionId,
      [PROPERTIES.TIMESTAMP]: new Date().toISOString(),
    });
  }

  /** End session */
  private endSession() {
    this.track(EVENTS.SESSION_ENDED, {
      [PROPERTIES.USER_ID]: this.userId,
      [PROPERTIES.SESSION_ID]: this.sessionId,
      [PROPERTIES.TIMESTAMP]: new Date().toISOString(),
    });
    localStorage.removeItem(PROPERTIES.SESSION_ID);
  }

  /** Capture device and browser info */
  private captureDeviceInfo() {
    const ua = navigator.userAgent;
    const isMobile = /Mobi|Android/i.test(ua);

    this.track(EVENTS.DEVICE_INFO_CAPTURED, {
      [PROPERTIES.USER_ID]: this.userId,
      [PROPERTIES.DEVICE_TYPE]: isMobile ? 'mobile' : 'desktop',
      [PROPERTIES.BROWSER]: this.getBrowserName(),
      [PROPERTIES.BROWSER_VERSION]: this.getBrowserVersion(),
      [PROPERTIES.OS]: this.getOS(),
      [PROPERTIES.TIMESTAMP]: new Date().toISOString(),
    });
  }

  /** Detect hard page refresh */
  private detectHardRefresh() {
    if (performance?.navigation?.type === 1) {
      this.track(EVENTS.HARD_PAGE_REFRESH, {
        [PROPERTIES.USER_ID]: this.userId,
        [PROPERTIES.SESSION_ID]: this.sessionId,
        [PROPERTIES.TIMESTAMP]: new Date().toISOString(),
      });
    }
  }

  /** Initialize unified user activity tracking (clicks, scrolls, mousemove, input typing) */
  public initUserActivityTracking(
    throttleMsClick: number = 3000,
    throttleMsScroll: number = 5000,
    throttleMsMouse: number = 5000,
    throttleMsInput: number = 1000
  ) {
    // Input typing (keyup event)
    fromEvent(document, 'keyup')
      .pipe(debounceTime(throttleMsInput))
      .subscribe((event: Event) => this.handleInputTyping(event as KeyboardEvent));
  }

  public fireLoginEvent() {
    this.track(EVENTS.USER_LOGGED_IN, {});
  }

  /** Handle input typing events */
  private handleInputTyping(event: KeyboardEvent) {
    const target = event.target as HTMLElement;

    // Only track typing in input fields or textareas
    if (target.getAttribute("id")?.includes("track") && target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      const inputElement = target as HTMLInputElement | HTMLTextAreaElement;
      const inputName = inputElement.id
      const inputValue = inputElement.value || '';
      const splitteInputName = inputName.split("-");
      const eventKey = splitteInputName[1] as keyof typeof EVENTS;
      const eventName = EVENTS[eventKey];
      // Track typing activity
      if (eventName) {
        this.track(eventName, {
          [PROPERTIES.FILTER_VALUE]: inputValue.substring(0, 50),
        });
      }
    }
  }

  /** Handle global click events */
  private handleGlobalClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    
    // Check for input elements (text, search, email, etc.)
    if (target.tagName === 'INPUT') {
      const inputElement = target as HTMLInputElement;
      const inputType = inputElement.type || 'text';
      const inputName = inputElement.name || inputElement.id || 'unnamed-input';
      const inputValue = inputElement.value || '';

      return;
    }

    // Check for button or link elements
    const button = target.tagName === 'BUTTON' ? target : target.closest('button, [role="button"], a');
    if (!button) return;

    const featureName =
      button.getAttribute('data-analytics')?.trim() || button.textContent?.trim() || 'UnknownButton';
  }

  /** Track scroll or mouse activity */
  private trackUserActivity(activityType: 'scroll' | 'mousemove') {
    this.track(EVENTS.USER_ACTIVITY, {
      [PROPERTIES.USER_ID]: this.userId,
      [PROPERTIES.ACTION]: activityType,
      [PROPERTIES.TIMESTAMP]: new Date().toISOString(),
    });
  }

  /** Convert URL path to human-readable page name */
  private getReadablePageName(url: string): string {
    // Remove query parameters and hash
    const path = url.split('?')[0].split('#')[0];
    
    // Custom route-to-name mappings
    const routeMap: Record<string, string> = {
      '/pages/ems/imo-dcs': 'EMS - IMO DCS',
      '/pages/ems/eu-mrv': 'EMS - EU MRV',
      '/pages/ems/cii': 'EMS - CII',
      '/pages/time-charterer': 'Time Charterer',
      '/pages/voyage': 'Voyage',
      '/pages/fleet': 'Fleet',
      '/pages/vessel': 'Vessel',
      '/pages/dashboard': 'Dashboard',
      '/login': 'Login',
      '/': 'Home',
      // Add more custom mappings here as needed
    };

    // Check for exact match first
    if (routeMap[path]) {
      return routeMap[path];
    }

    // Check for partial matches (e.g., /pages/vessel/123)
    for (const [route, name] of Object.entries(routeMap)) {
      if (path.startsWith(route)) {
        return name;
      }
    }

    // Fallback: Convert path to readable format
    // e.g., /pages/some-page-name -> Some Page Name
    return path
      .split('/')
      .filter(segment => segment && segment !== 'pages')
      .map(segment => 
        segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      )
      .join(' - ') || 'Home';
  }

  /** Utility: generate session ID */
  private generateSessionId(): string {
    return 'session-' + Math.random().toString(36).substr(2, 9);
  }

  /** Browser helpers */
  private getBrowserName(): string {
    const ua = navigator.userAgent;
    if (/chrome|crios|crmo/i.test(ua)) return 'Chrome';
    if (/firefox|fxios/i.test(ua)) return 'Firefox';
    if (/safari/i.test(ua)) return 'Safari';
    if (/edg/i.test(ua)) return 'Edge';
    return 'Unknown';
  }

  private getBrowserVersion(): string {
    const match = navigator.userAgent.match(/(firefox|fxios|chrome|crios|crmo|safari|edg)\/? (\d+)/i);
    return match ? match[2] : 'Unknown';
  }

  private getOS(): string {
    const ua = navigator.userAgent;
    if (/Windows NT/i.test(ua)) return 'Windows';
    if (/Mac OS X/i.test(ua)) return 'MacOS';
    if (/Android/i.test(ua)) return 'Android';
    if (/iPhone|iPad/i.test(ua)) return 'iOS';
    return 'Unknown';
  }


 
}
